import { Button, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import tokens from '../shared/theme/tokens';
import { useTranslation } from 'react-i18next';

export default function Pagination({ totalPage, currentPage, rowsPerPage, onChangeRowsPerPage, onChangeCurrentPage }) {
  const { t } = useTranslation();
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const commitPage = () => {
    const next = Number.parseInt(pageInput, 10);
    if (!Number.isInteger(next) || next < 1 || next > totalPage) {
      setPageInput(String(currentPage));
      return;
    }
    if (next !== currentPage) onChangeCurrentPage(next);
  };

  const handlePrevPage = () => {
    onChangeCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    onChangeCurrentPage(currentPage + 1);
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={4} sx={{ mt: 1.5 }}>
      <Stack direction="row" gap={1} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          disabled={currentPage === 1}
          onClick={handlePrevPage}
          sx={{
            p: 1,
            borderRadius: 1,
            border: `1px solid ${tokens.colors.border}`,
            width: '26px',
            height: '26px',
            minWidth: 'auto',
            color: tokens.colors.ink,
            '&:hover': { backgroundColor: tokens.colors.hover },
          }}
        >
          <ChevronLeftOutlinedIcon />
        </Button>
        <TextField
          value={pageInput}
          onChange={e => setPageInput(e.target.value.replace(/\D/g, ''))}
          onBlur={commitPage}
          onFocus={e => e.target.select()}
          onKeyDown={e => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          inputProps={{ 'aria-label': 'Page', inputMode: 'numeric' }}
          sx={{
            width: 48,
            '& .MuiOutlinedInput-root': {
              height: 26,
              fontSize: 12,
              fontWeight: 600,
              color: tokens.colors.primary,
              '& fieldset': { borderColor: tokens.colors.primary },
            },
            '& input': { textAlign: 'center', padding: '2px 4px' },
          }}
        />
        <Typography variant="caption">/</Typography>
        <Typography variant="caption">{totalPage}</Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={currentPage === totalPage}
          onClick={handleNextPage}
          sx={{
            p: 1,
            borderRadius: 1,
            border: `1px solid ${tokens.colors.border}`,
            width: '26px',
            height: '26px',
            minWidth: 'auto',
            color: tokens.colors.ink,
            '&:hover': { backgroundColor: tokens.colors.hover },
          }}
        >
          <ChevronRightOutlined />
        </Button>
      </Stack>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="caption">{t('pagination.rowsPerPage')}</Typography>
        <Select size="small" value={rowsPerPage} onChange={e => onChangeRowsPerPage(e.target.value)}>
          {rowOptions.map(row => (
            <MenuItem key={row} value={row}>
              {row}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
}

const rowOptions = [10, 25, 50];
